import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  BookingArrivalStatus,
  BookingCurrentEstimatedArrivalWindowRecord,
  BookingRecordDetailsCustomer
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiSkeleton } from 'noddi-ui-web';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';

export function BookingEstimatedArrivalInfo({
  booking,
  isActiveStep
}: {
  booking: BookingRecordDetailsCustomer;
  isActiveStep: boolean;
}) {
  const {
    data: currentEstimatedArrivalTime,
    isPending: isEstimatePending,
    error: currentEstimatedArrivalTimeError
  } = noddiAsync.useGet({
    type: URLKeys.getCurrentEstimatedArrivalTime,
    input: { slug: booking.slug }
  });

  if (isEstimatePending) {
    return <NoddiSkeleton className='h-8 w-full' />;
  }

  if (currentEstimatedArrivalTimeError) {
    return null;
  }

  return (
    <div className={cn(isActiveStep ? 'text-black' : 'text-muted-foreground', 'mb-2')}>
      <ArrivalTime arrival={currentEstimatedArrivalTime} />
    </div>
  );
}

const ArrivalTime = ({ arrival }: { arrival: BookingCurrentEstimatedArrivalWindowRecord }) => {
  const { start, end, status } = arrival;
  if (start && end && status) {
    const arrivalStart = noddiFormatDate(start, NoddiDateFormats.TIME);
    const arrivalEnd = noddiFormatDate(end, NoddiDateFormats.TIME);

    // An estimate, never a promise: the route moves during the day.
    if (status === BookingArrivalStatus.BEHIND) {
      return (
        <p className='text-sm'>
          <Trans>
            We are a bit behind schedule. We now think we will arrive between <strong>{arrivalStart}</strong> and{' '}
            <strong>{arrivalEnd}</strong>.
          </Trans>
        </p>
      );
    }
    if (status === BookingArrivalStatus.AHEAD) {
      return (
        <p className='text-sm'>
          <Trans>
            We are a bit ahead of schedule. We now think we will arrive between <strong>{arrivalStart}</strong> and{' '}
            <strong>{arrivalEnd}</strong>.
          </Trans>
        </p>
      );
    }
    return (
      <p className='text-sm'>
        <Trans>
          We currently estimate that we will arrive between <strong>{arrivalStart}</strong> and{' '}
          <strong>{arrivalEnd}</strong>. We will let you know when we are on our way.
        </Trans>
      </p>
    );
  }

  return (
    <p className='text-sm'>
      {t`The day before, we will text you when we expect to arrive. We will also let you know when we are on our way.`}
    </p>
  );
};
